import { useState } from "react";
import useWebTracking from "@/hooks/useWebTracking";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import api, { errMsg, fieldErrors } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import GoogleSignInButton from "@/components/GoogleSignInButton";

function FieldError({ msg, testid }) {
    if (!msg) return null;
    return (
        <div className="mt-1.5 flex items-start gap-1 text-xs text-rose-600"
            data-testid={testid}>
            <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
            <span>{msg}</span>
        </div>
    );
}

export default function Signup() {
    useWebTracking();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        mobile: "",
        password: "",
        medical_registration_number: "",
        specialization: "",
        city: "",
        state: "",
        country: "India",
        terms_accepted: false,
    });

    const set = (k) => (e) => {
        setForm({ ...form, [k]: e.target ? e.target.value : e });
        if (errors[k]) setErrors((p) => { const n = { ...p }; delete n[k]; return n; });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.terms_accepted) {
            toast.error("Please accept the Terms & Conditions to continue.");
            return;
        }
        setBusy(true);
        setErrors({});
        try {
            const { data } = await api.post("/auth/signup", form);
            toast.success("Account created — check the Dev OTP toast on the next screen", {
                description: `Dev OTP: ${data.dev_otp}`,
                duration: 8000,
            });
            sessionStorage.setItem("ddi_signup_email", data.user.email);
            sessionStorage.setItem("ddi_dev_otp", data.dev_otp);
            nav("/verify-otp");
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) {
                setErrors(fe);
                toast.error("Please check the highlighted fields.");
            } else {
                toast.error(errMsg(err, "Signup failed. Please check your details."));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
            <div className="ddi-auth-panel text-white hidden lg:flex flex-col justify-between p-12 relative">
                <Link to="/" className="inline-flex items-center gap-2.5 text-lg font-bold relative z-10">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                    <span>DOCTOR <span className="text-[#60A5FA]">DIGITAL ID</span></span>
                </Link>
                <div className="max-w-md relative z-10">
                    <div className="ddi-eyebrow text-[#60A5FA] mb-3">Start in 2 minutes</div>
                    <h1 className="text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight">
                        Build a verified identity your patients will remember.
                    </h1>
                    <p className="mt-5 text-white/75 text-[15px] leading-relaxed">
                        Publish a professional profile at{" "}
                        <span className="font-mono text-white">/dr/&lt;you&gt;</span> — AI-refined, admin-reviewed, and approved by you.
                    </p>
                    <ul className="mt-8 space-y-2.5 max-w-sm text-[13.5px] text-white/85">
                        <li className="flex items-start gap-2.5">
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#60A5FA] shrink-0" />
                            <span>Grounded AI — never fabricates credentials.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#60A5FA] shrink-0" />
                            <span>Two-stage approval — admin then doctor.</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#60A5FA] shrink-0" />
                            <span>Public URL, QR visiting card, verified badge.</span>
                        </li>
                    </ul>
                </div>
                <div className="text-xs text-white/50 relative z-10">
                    Demo build · No real patient data collected.
                </div>
            </div>
            <div className="flex items-center justify-center p-6 md:p-12 bg-[#F5F7FB]">
                <form
                    onSubmit={submit}
                    className="ddi-card ddi-shadow-md p-8 sm:p-9 w-full max-w-xl"
                    data-testid="signup-form"
                >
                    <Link to="/" className="flex items-center gap-2.5 w-fit lg:hidden mb-6" data-testid="signup-brand-mobile">
                        <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-9 w-9 object-contain" />
                        <span className="text-[14px] font-bold tracking-tight text-[#123B5D]">
                            DOCTOR <span className="text-[#2563EB]">DIGITAL ID</span>
                        </span>
                    </Link>
                    <div className="mb-6">
                        <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">
                            Create account
                        </div>
                        <h2 className="mt-1 text-2xl font-bold text-[#123B5D]">
                            Register as a doctor
                        </h2>
                    </div>
                    <div className="mb-6">
                        <GoogleSignInButton label="Sign up with Google" testId="signup-google-btn" />
                        <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-widest text-[#17212B]/40">
                            <div className="flex-1 h-px bg-[#EEF1F6]" />
                            <span>or with email</span>
                            <div className="flex-1 h-px bg-[#EEF1F6]" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <Label htmlFor="full_name">Full name</Label>
                            <Input
                                id="full_name"
                                value={form.full_name}
                                onChange={set("full_name")}
                                placeholder="Dr. Jane Doe"
                                data-testid="signup-full-name"
                                aria-invalid={!!errors.full_name}
                                required
                            />
                            <FieldError msg={errors.full_name} testid="signup-full-name-error" />
                        </div>
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                placeholder="you@clinic.com"
                                data-testid="signup-email"
                                aria-invalid={!!errors.email}
                                required
                            />
                            <FieldError msg={errors.email} testid="signup-email-error" />
                        </div>
                        <div>
                            <Label htmlFor="mobile">Mobile</Label>
                            <Input
                                id="mobile"
                                value={form.mobile}
                                onChange={set("mobile")}
                                placeholder="+91 9XXXXXXXXX"
                                data-testid="signup-mobile"
                                aria-invalid={!!errors.mobile}
                                required
                            />
                            <FieldError msg={errors.mobile} testid="signup-mobile-error" />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={form.password}
                                onChange={set("password")}
                                placeholder="Min 8 characters"
                                data-testid="signup-password"
                                aria-invalid={!!errors.password}
                                required
                            />
                            <FieldError msg={errors.password} testid="signup-password-error" />
                        </div>
                        <div>
                            <Label htmlFor="reg">Medical registration number</Label>
                            <Input
                                id="reg"
                                value={form.medical_registration_number}
                                onChange={set("medical_registration_number")}
                                placeholder="KA-12345"
                                data-testid="signup-reg"
                                aria-invalid={!!errors.medical_registration_number}
                                required
                            />
                            <FieldError msg={errors.medical_registration_number} testid="signup-reg-error" />
                        </div>
                        <div>
                            <Label htmlFor="spec">Specialization</Label>
                            <Input
                                id="spec"
                                value={form.specialization}
                                onChange={set("specialization")}
                                placeholder="Cardiology"
                                data-testid="signup-spec"
                                aria-invalid={!!errors.specialization}
                                required
                            />
                            <FieldError msg={errors.specialization} testid="signup-spec-error" />
                        </div>
                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input
                                id="city"
                                value={form.city}
                                onChange={set("city")}
                                data-testid="signup-city"
                                aria-invalid={!!errors.city}
                                required
                            />
                            <FieldError msg={errors.city} testid="signup-city-error" />
                        </div>
                        <div>
                            <Label htmlFor="state">State</Label>
                            <Input
                                id="state"
                                value={form.state}
                                onChange={set("state")}
                                data-testid="signup-state"
                                aria-invalid={!!errors.state}
                                required
                            />
                            <FieldError msg={errors.state} testid="signup-state-error" />
                        </div>
                        <div>
                            <Label htmlFor="country">Country</Label>
                            <Input
                                id="country"
                                value={form.country}
                                onChange={set("country")}
                                data-testid="signup-country"
                                aria-invalid={!!errors.country}
                                required
                            />
                            <FieldError msg={errors.country} testid="signup-country-error" />
                        </div>
                    </div>
                    <label className="mt-5 flex items-start gap-2 text-sm text-[#17212B]/80">
                        <Checkbox
                            checked={form.terms_accepted}
                            onCheckedChange={(v) =>
                                setForm({ ...form, terms_accepted: !!v })
                            }
                            data-testid="signup-terms"
                            className="mt-1"
                        />
                        <span>
                            I accept the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#0F766E] font-semibold hover:underline">Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#0F766E] font-semibold hover:underline">Privacy Policy</a>. I understand this is a professional identity platform, not a diagnostic service.
                        </span>
                    </label>
                    <button
                        type="submit"
                        className="mt-6 w-full ddi-teal-btn disabled:opacity-60"
                        disabled={busy}
                        data-testid="signup-submit"
                    >
                        {busy ? "Creating…" : "Create account"}
                    </button>
                    <div className="mt-4 text-sm text-[#17212B]/70 text-center">
                        Already have an account?{" "}
                        <Link to="/login" className="text-[#0F766E] font-semibold hover:underline" data-testid="signup-goto-login">
                            Log in
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
